import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { ApplePay } from 'checkout/components/Payment/ApplePay';
import { Affirm } from 'checkout/components/Payment/Affirm';
import { Afterpay } from 'checkout/components/Payment/Afterpay';
import { Alipay } from 'checkout/components/Payment/Alipay';
import { CreditCard } from 'checkout/components/Payment/CreditCard';
import { Giropay } from 'checkout/components/Payment/Giropay';
import { Klarna } from 'checkout/components/Payment/Klarna';
import { Loader } from 'shared/components/Loader';
import { PayPal } from 'checkout/components/Payment/v2/PayPal';
import { PaymentSelector } from 'checkout/components/Payment/PaymentSelector';
import { PaymentVendor } from 'checkout/enums/PaymentVendor';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useCurrency } from 'shared/hooks/useCurrency';
import { TabPanelWrapper } from 'checkout/components/Tabs/Components';
import { RequirementsNotMetModal } from 'checkout/components/Payment/RequirementsNotMetModal';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import {
  shouldShowAffirm,
  shouldShowAfterpay,
  shouldShowKlarna,
  shouldShowAlipay,
  shouldShowApplePay,
  shouldShowPayPal,
} from 'checkout/utils/orderUtils';
import { GiftCardEventTypes } from 'gift-cards/enums/constants';
import { guestPurchasingGiftCard, purchasingGiftCard } from 'checkout/utils/giftCards';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.Tabs.PaymentTab';

export const PaymentTab = () => {
  const router = useRouter();

  const { isAuthenticated } = useStoreSelector((state) => state.auth);
  const { order, loading, isGuestCheckout, applePayAvailable, availablePaymentMethods } =
    useCheckoutContext();
  const {
    affirm: affirmAvailability,
    afterpay: afterpayAvailability,
    alipay: alipayAvailability,
    klarna: klarnaAvailability,
    paypal: paypalAvailability,
  } = availablePaymentMethods;

  const { selectedCurrencyCode } = useCurrency();

  const [paymentSelection, setPaymentSelection] = useState(PaymentVendor.CreditCard);
  const [showRequirementsNotMetModal, setShowRequirementsNotMetModal] = useState(false);
  const [paymentRequirementsNotMet, setPaymentRequirementsNotMet] = useState(null);

  const isCheckoutPurchaseGiftCardsEnabled = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_PURCHASE_GIFT_CARDS,
  );
  const isWebEnableCheckoutGiftCardsAllowGuestPurchase = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_GIFT_CARDS_ALLOW_GUEST_PURCHASE,
  );

  const isGuestPurchasingGiftCard = guestPurchasingGiftCard({
    giftCardId: router?.query?.giftCardId,
    isAuthenticated,
    isGuestCheckout,
    isWebEnableCheckoutGiftCardsAllowGuestPurchase,
  });
  const isPurchasingGiftCard = purchasingGiftCard({
    giftCardId: order?.product?.giftCardId,
    isGuestPurchasingGiftCard,
    isCheckoutPurchaseGiftCardsEnabled,
  });

  let giftCardType = GiftCardEventTypes.NULL;
  if (order?.product?.giftCardId) {
    giftCardType = GiftCardEventTypes.PURCHASED;
  }
  if (order?.giftCards?.length > 0) {
    giftCardType = GiftCardEventTypes.APPLIED;
  }

  const getPaymentView = () => {
    switch (paymentSelection) {
      case PaymentVendor.Affirm:
        return <Affirm />;
      case PaymentVendor.AfterPay:
        return <Afterpay />;
      case PaymentVendor.AliPay:
        return <Alipay />;
      case PaymentVendor.ApplePay:
        return <ApplePay />;
      case PaymentVendor.Giropay:
        return <Giropay />;
      case PaymentVendor.Klarna:
        return <Klarna selectPaymentVendor={setPaymentSelection} />;
      case PaymentVendor.PayPal:
        return <PayPal />;
      default:
        return <CreditCard />;
    }
  };

  const handlePaymentSelectClick = (paymentVendor: PaymentVendor) => {
    setPaymentSelection(paymentVendor);

    sendTrackingEvent('CHECKOUT_PAYMENT_TAP', {
      payment_type: paymentVendor,
      product_template_slug: order?.product?.productTemplate?.slug,
    });
  };

  let ready = !loading.order;

  /**
   * NOTE: if a guest user is purchasing a gift card we will not have an order
   * ready at this point. must allow them to continue
   */
  if (isGuestPurchasingGiftCard) {
    ready = true;
  }

  useEffect(() => {
    sendTrackingEvent('CHECKOUT_VIEW', {
      box_condition: order?.product?.boxCondition,
      condition_category: order?.product?.shoeCondition,
      currency_code: selectedCurrencyCode,
      instant_ship: order?.isInstantShip,
      order_number: order?.number,
      price: order?.localizedPriceCents?.amount,
      product_template_slug: order?.product?.productTemplate?.slug,
      shoe_condition: order?.product?.shoeCondition,
      size: order?.product?.size,
      step: 'payment',
      gift_card_type: giftCardType,
    });
  }, [order?.id]);

  useEffect(() => {
    if (!isCheckoutPurchaseGiftCardsEnabled) return;

    const showAffirm = shouldShowAffirm({
      affirmAvailability,
      isPurchasingGiftCard,
    });

    const showAfterpay = shouldShowAfterpay({
      afterpayAvailability,
      isPurchasingGiftCard,
    });

    const showAlipay = shouldShowAlipay({
      alipayAvailability,
      isPurchasingGiftCard,
    });

    const showApplePay = shouldShowApplePay({
      applePayAvailable,
      order,
    });

    const showKlarna = shouldShowKlarna({
      klarnaAvailability,
      isPurchasingGiftCard,
    });

    const showPayPal = shouldShowPayPal({
      paypalAvailability,
      isPurchasingGiftCard,
    });

    const onAffirm = paymentSelection === PaymentVendor.Affirm;
    const onAfterpay = paymentSelection === PaymentVendor.AfterPay;
    const onKlarna = paymentSelection === PaymentVendor.Klarna;
    const onPayPal = paymentSelection === PaymentVendor.PayPal;
    const onAliPay = paymentSelection === PaymentVendor.AliPay;
    const onApplePay = paymentSelection === PaymentVendor.ApplePay;

    // In case a payment method was highlighted but no longer available as an option, we default to highlight Credit Card.
    if ((!showPayPal && onPayPal) || (!showAlipay && onAliPay) || (!showApplePay && onApplePay)) {
      setPaymentSelection(PaymentVendor.CreditCard);
      // No warning popup messages for now.
    }

    if ((!showAffirm && onAffirm) || (!showAfterpay && onAfterpay) || (!showKlarna && onKlarna)) {
      setShowRequirementsNotMetModal(true);
      setPaymentSelection(PaymentVendor.CreditCard);

      if (onAffirm) {
        setPaymentRequirementsNotMet(PaymentVendor.Affirm);
      }

      if (onAfterpay) {
        setPaymentRequirementsNotMet(PaymentVendor.AfterPay);
      }

      if (onKlarna) {
        setPaymentRequirementsNotMet(PaymentVendor.Klarna);
      }
    }
  }, [order?.localizedFinalPriceCents?.amountUsdCents]);

  return (
    <TabPanelWrapper>
      {ready ? (
        <>
          <PaymentSelector
            onPaymentSelectClick={handlePaymentSelectClick}
            selection={paymentSelection}
          />
          {getPaymentView()}
          {showRequirementsNotMetModal && (
            <RequirementsNotMetModal onClose={() => setShowRequirementsNotMetModal(false)}>
              <>
                {paymentRequirementsNotMet === PaymentVendor.Affirm &&
                  t(
                    `${TP}.requirementsNotMetAffirmBody`,
                    'Affirm is only available as a payment method when the order total is $50 or more.',
                  )}
                {paymentRequirementsNotMet === PaymentVendor.AfterPay &&
                  t(
                    `${TP}.requirementsNotMetAfterpayBody`,
                    'Afterpay is only available as a payment method when the total is $35-$1000.',
                  )}
                {paymentRequirementsNotMet === PaymentVendor.Klarna &&
                  t(
                    `${TP}.requirementsNotMetKlarnaBody`,
                    'Klarna is only available as a payment method when the total is $35-$10,000.',
                  )}
              </>
            </RequirementsNotMetModal>
          )}
        </>
      ) : (
        <Loader />
      )}
    </TabPanelWrapper>
  );
};
